import React, { useState } from "react";

import { Link } from "react-router-dom";

import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Modal,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";

import { Dropzone, IMAGE_MIME_TYPE, MIME_TYPES } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { Grid } from "@mui/material";
import {
  IconEdit,
  IconEye,
  IconPhoto,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import MegaLoader from "../../Components/MegaLoader";
import { Endpoints } from "../../Lib/endpoints";
import { PaginatedResponse } from "../../Lib/responses";
import { BlogProps, PaginationProps } from "../../Lib/types";
import {
  PerformRequest,
  UploadFiles,
  usePerformRequest,
} from "../../Lib/usePerformRequest";
import "./styles.scss";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
} from "../../Lib/methods";
import { HttpSuccessCodesObj } from "../../Lib/data";

dayjs.extend(relativeTime);

const Blogs: React.FC = () => {
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 30,
  });
  const {
    data: blogs,
    isLoading: isLoadingBlogs,
    reloadData: reloadBlogs,
  } = usePerformRequest<PaginatedResponse<BlogProps[]>>({
    method: Endpoints.GetBlogs(pagination.page, pagination.limit).method,
    route: Endpoints.GetBlogs(pagination.page, pagination.limit).route,
  });
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const blogForm = useForm<Partial<BlogProps>>({
    initialValues: {
      title: "",
      description: "",
      thumbnail: "",
      tags: [],
    },
    validate: {
      title: (v) => (v && v.length >= 3 ? null : "Title is too short"),
      description: (v) =>
        v && v.length >= 5 ? null : "Description is too short",
      tags: (v) => (v && v.length >= 1 ? null : "At least one tag is required"),
      thumbnail: (v) =>
        !v && v?.length === 0 && !thumbnail ? "Thumbnail is required" : null,
    },
  });
  const [
    isDeleteModalOpened,
    { open: OpenDeleteModal, close: CloseDeleteModal },
  ] = useDisclosure(false);

  const [isBlogModalOpened, { open: OpenBlogModal, close: CloseBlogModal }] =
    useDisclosure(false);

  const [blogModalAction, setBlogModalAction] = useState<"create" | "edit">(
    "create"
  );

  const CancelBlogModal = () => {
    CloseBlogModal();
    blogForm.reset();
    setThumbnail(null);
  };

  const [isCreatingOrUpdatingBlog, setCreatingOrUpdatingBlog] = useState(false);
  const CreateNewBlog = async () => {
    const { hasErrors } = blogForm.validate();

    if (!hasErrors) {
      setCreatingOrUpdatingBlog(true);
      const { title, description, tags } = blogForm.values;
      const data = {
        title,
        description,
        tags,
      };

      const createBlogAction = async (thumbnail: string) => {
        const r = await PerformRequest({
          method: Endpoints.GetCreateBlog().method,
          route: Endpoints.GetCreateBlog().route,
          data: {
            ...data,
            thumbnail,
          },
        });
        if (r) {
          if (r.status === 201) {
            setCreatingOrUpdatingBlog(false);
            DefaultSuccessNotification("Blog created successfully!");
            CloseBlogModal();
            reloadBlogs();
          } else {
            setCreatingOrUpdatingBlog(false);
            DefaultErrorNotification(r.message ?? "Could not create blog!");
          }
        }
      };
      UploadFiles([thumbnail as any])
        .then((uploaded) => {
          if (uploaded) {
            if (uploaded.status === 201) {
              createBlogAction(uploaded.data[0].path);
            } else {
              setCreatingOrUpdatingBlog(false);
              DefaultErrorNotification("Could not upload image!");
            }
          } else {
            setCreatingOrUpdatingBlog(false);
            DefaultErrorNotification("Could not upload image!");
          }
        })
        .catch((e) => {
          setCreatingOrUpdatingBlog(false);
          DefaultErrorNotification("Could not upload image!");
        });
    }
    console.log(blogForm.values);
  };

  const [selectedBlog, setSelectedBlog] = useState<BlogProps | null>(null);

  const handleDeleteClick = (blog: BlogProps) => {
    setSelectedBlog(blog);
    OpenDeleteModal();
  };

  const [isDeletingBlog, setDeletingBlog] = useState(false);
  const DeleteBlog = async () => {
    setDeletingBlog(true);
    const r = await PerformRequest({
      method: Endpoints.GetDeleteBlog(selectedBlog?.id as string).method,
      route: Endpoints.GetDeleteBlog(selectedBlog?.id as string).route,
      callback: () => {
        setDeletingBlog(false);
      },
    });
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        setDeletingBlog(false);
        DefaultSuccessNotification("Blog deleted successfully!");
        CloseDeleteModal();
        reloadBlogs();
      } else {
        setDeletingBlog(false);
        DefaultErrorNotification(r.message ?? "Could not delete blog!");
      }
    }
  };

  const blogFormInputProps = {
    disabled: isCreatingOrUpdatingBlog,
    loading: isCreatingOrUpdatingBlog,
    withAsterisk: true,
  };

  return (
    <div
      className="blogs-container"
      style={{
        minHeight: "100vh",
        width: "100%",
        padding: "40px 0",
        background: "#f8f9fa",
      }}
    >
      <Container mx="auto">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 32,
          }}
        >
          <Text fw={800} fz={24} c="#4b2996" style={{ letterSpacing: 0.5 }}>
            Blogs
          </Text>
          <Group gap={10}>
            <Button
              disabled={isLoadingBlogs}
              onClick={reloadBlogs}
              variant="default"
              leftSection={<IconRefresh size={18} />}
              radius={8}
            >
              Refresh
            </Button>
            <Button
              disabled={isLoadingBlogs}
              color="grape"
              leftSection={<IconPlus size={18} />}
              radius={8}
              onClick={() => {
                setBlogModalAction("create");
                OpenBlogModal();
                blogForm.reset();
                setThumbnail(null);
              }}
            >
              New
            </Button>
          </Group>
        </div>
        {isLoadingBlogs && <MegaLoader />}
        {blogs && blogs.data && (
          <>
            {blogs.data.length > 0 ? (
              <Grid container spacing={5}>
                {blogs?.data?.map((blog) => (
                  <Grid item xs={12} sm={6} md={4} key={blog.id}>
                    <Card
                      withBorder
                      radius={10}
                      style={{
                        background: "#fff",
                        border: "1.5px solid #e9ecef",
                        overflow: "hidden",
                        height: 310,
                        maxWidth: 410,
                        margin: "0 auto",
                        boxShadow: "0 2px 12px 0 rgba(80,60,180,0.06)",
                        display: "flex",
                        flexDirection: "column",
                        padding: 0,
                        opacity: isLoadingBlogs ? 0.5 : 1,
                      }}
                    >
                      <img
                        src={blog.thumbnail}
                        alt={blog.title}
                        style={{
                          width: "100%",
                          height: 110,
                          objectFit: "cover",
                          borderTopLeftRadius: 10,
                          borderTopRightRadius: 10,
                        }}
                      />
                      <div
                        style={{
                          padding: 14,
                          paddingBottom: 0,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          height: "100%",
                        }}
                      >
                        <Badge
                          color="#228be6"
                          variant="light"
                          size="md"
                          radius="xl"
                          style={{
                            background: "#e3f0ff",
                            color: "#228be6",
                            fontWeight: 600,
                            fontSize: 12,
                            marginBottom: 10,
                            letterSpacing: 0.3,
                            padding: "0 10px",
                          }}
                        >
                          {blog.tags[0]?.toUpperCase()}
                        </Badge>
                        <Text
                          fw={700}
                          fz={16}
                          mb={10}
                          style={{ lineHeight: 1.22 }}
                        >
                          {blog.title}
                        </Text>
                        <Group align="center" mb={12} gap={8}>
                          <Avatar
                            src="https://api.dicebear.com/7.x/adventurer/svg?seed=Elsa"
                            radius="md"
                            size={28}
                          />
                          <div>
                            <Text fw={600} fz={13} style={{ marginBottom: 1 }}>
                              {blog.createdBy}
                            </Text>
                            <Text fz={12} c="#adb5bd">
                              posted {dayjs(blog.createdAt).fromNow()}
                            </Text>
                          </div>
                        </Group>
                      </div>
                      <Divider my={0} />
                      <Group
                        justify="space-between"
                        align="center"
                        px={14}
                        py={8}
                      >
                        <Text fz={12} c="#adb5bd">
                          {blog.likes.length} people liked this
                        </Text>
                        <Group gap={6}>
                          <Link to={`/d/blog-view/${blog.id}`}>
                            <ActionIcon
                              disabled={isLoadingBlogs}
                              variant="subtle"
                              color="blue"
                              size="md"
                            >
                              <IconEye size={16} />
                            </ActionIcon>
                          </Link>
                          <Link to={`/d/blog-edit/${blog.id}`}>
                            <ActionIcon
                              disabled={isLoadingBlogs}
                              variant="subtle"
                              color="yellow"
                              size="md"
                            >
                              <IconEdit size={16} />
                            </ActionIcon>
                          </Link>
                          <ActionIcon
                            disabled={isLoadingBlogs}
                            variant="subtle"
                            color="red"
                            size="md"
                            onClick={() => handleDeleteClick(blog)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Group>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Text
                fw={700}
                fz={18}
                c="#adb5bd"
                style={{ textAlign: "center" }}
              >
                No blogs found
              </Text>
            )}
          </>
        )}
      </Container>
      {/* Delete Modal */}
      <Modal
        opened={isDeleteModalOpened}
        onClose={CloseDeleteModal}
        centered
        title={
          <Text fw={700} fz={18}>
            Delete Blog
          </Text>
        }
        radius={12}
        size="md"
        padding="lg"
      >
        <Text fz={15} mb={18}>
          Are you sure you want to delete <b>{selectedBlog?.title}</b>? This
          action cannot be undone.
        </Text>
        <Group justify="flex-end" mt={24}>
          <Button variant="default" onClick={CloseDeleteModal} radius={8}>
            Cancel
          </Button>
          <Button color="red" radius={8} onClick={DeleteBlog}>
            Delete
          </Button>
        </Group>
      </Modal>
      <Modal
        opened={isBlogModalOpened}
        onClose={CancelBlogModal}
        centered
        title={
          <Text fw={600} fz={18}>
            {blogModalAction === "create" ? "Create New Blog" : "Edit Blog"}
          </Text>
        }
        radius={14}
        padding="lg"
        size="lg"
        className="blog-modal"
      >
        <TextInput
          label="Title"
          placeholder="Enter blog title"
          mb={12}
          {...blogFormInputProps}
          {...blogForm.getInputProps("title")}
        />

        <Textarea
          label="Short Description"
          placeholder="Enter a short description"
          mb={12}
          {...blogFormInputProps}
          {...blogForm.getInputProps("description")}
        />
        <TagsInput
          mb={12}
          label="Press Enter to submit the tag"
          placeholder="Enter tag"
          maxTags={1}
          {...blogFormInputProps}
          {...blogForm.getInputProps("tags")}
        />
        <Dropzone
          {...blogFormInputProps}
          style={{
            borderColor: blogForm.errors.thumbnail ? "red" : undefined,
          }}
          onDrop={(files) => {
            setThumbnail(files[0]);
            if (files && files[0]) {
              blogForm.setFieldError("thumbnail", null);
            }
          }}
          onReject={(files) => console.log("rejected files", files)}
          maxSize={5 * 1024 ** 2}
          accept={[MIME_TYPES.jpeg, MIME_TYPES.png]}
          maxFiles={1}
        >
          {thumbnail ? (
            <div className="thumbnail-container">
              <img src={URL.createObjectURL(thumbnail)} alt="" />
            </div>
          ) : (
            <Group
              justify="center"
              gap="xl"
              mih={220}
              style={{ pointerEvents: "none" }}
            >
              <Dropzone.Accept>
                <IconUpload
                  size={52}
                  color="var(--mantine-color-blue-6)"
                  stroke={1.5}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX
                  size={52}
                  color="var(--mantine-color-red-6)"
                  stroke={1.5}
                />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto
                  size={52}
                  color="var(--mantine-color-dimmed)"
                  stroke={1.5}
                />
              </Dropzone.Idle>

              <div>
                <Text size="xl" inline>
                  Drag image here or click to select
                </Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  Select just ONE thumbnail. File should not exceed 5mb
                </Text>
              </div>
            </Group>
          )}
        </Dropzone>

        <Group justify="flex-end" mt={18}>
          <Button
            disabled={isCreatingOrUpdatingBlog}
            variant="default"
            onClick={CancelBlogModal}
            radius={8}
          >
            Cancel
          </Button>
          <Button
            loading={isCreatingOrUpdatingBlog}
            color="grape"
            type="submit"
            radius={8}
            onClick={() => {
              if (blogModalAction === "create") {
                CreateNewBlog();
              } else {
                // UpdateBlog();
              }
            }}
          >
            Create
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default Blogs;
